// PLAHER 6D radar chart for the individual feedback report (v6.5).
//
// Renders peer-mean and self-rating series on a six-axis polar chart.
// Input is the `signals` object produced server-side by signals.js:
//   { peer_means: { Performance, Leadership, Attitude, Harmony, Expression, Reliable },
//     self_ratings: same shape }
//
// Differs from ReportSpiderChart in that it takes the synthesis signals
// directly (not the legacy peerRating/selfRating structure) and uses the
// PLAHER 6D dimension order verbatim.

import React from 'react';
import { RadarChart as eRadarChart } from 'echarts/charts';
import {
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent,
} from 'echarts/components';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import PropTypes from 'prop-types';

import {
  ITPMETRICS_BLUE, ITPMETRICS_BLUE_LIGHT, ITPMETRICS_YELLOW, ITPMETRICS_YELLOW_LIGHT,
} from '../common/Colours';

echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  eRadarChart,
  CanvasRenderer,
  LegendComponent,
]);

const PLAHER_DIMS = ['Performance', 'Leadership', 'Attitude', 'Harmony', 'Expression', 'Reliable'];

const toAxisValue = (v) => {
  if (v === null || v === undefined) return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

export function PlaherRadarChart({ signals }) {
  if (!signals || !signals.peer_means) return null;

  // Profile-driven: the server ships signals.dims (ordered dimension keys) and
  // signals.dim_labels (display names) -- see individualReport/signals.js. Fall back to
  // the PLAHER 6D order + identity labels for any older cached synthesis that predates
  // these fields, so PLAHER rendering stays unchanged. ENT supplies its 6 competencies.
  const dims = (Array.isArray(signals.dims) && signals.dims.length) ? signals.dims : PLAHER_DIMS;
  const labelOf = (d) => (signals.dim_labels && signals.dim_labels[d]) || d;

  const peerValues = dims.map((d) => toAxisValue(signals.peer_means[d]));
  const selfValues = dims.map((d) => toAxisValue(signals.self_ratings && signals.self_ratings[d]));
  const indicator = dims.map((d) => ({ name: labelOf(d), max: 5 }));

  const hasSelf = selfValues.some((v) => v > 0);

  const series = [
    {
      name: 'Peer rating',
      itemStyle: { color: ITPMETRICS_BLUE },
      lineStyle: { width: 4 },
      // 2026-06-08: only the peer series is labelled with numbers. When peer and
      // self values are close at a point, two stacked numbers overlapped; showing
      // just the peer number (the actual feedback) keeps every point readable.
      // Self is conveyed by its shaded polygon; its exact values are in Section B.
      // White backing keeps a peer number legible where adjacent points crowd.
      label: {
        show: true,
        fontSize: 10,
        color: '#222222',
        backgroundColor: 'rgba(255,255,255,0.85)',
        padding: [1, 2],
        borderRadius: 2,
        formatter: (params) => (params.value ? params.value.toFixed(1) : ''),
      },
      areaStyle: { color: ITPMETRICS_BLUE_LIGHT },
      value: peerValues,
    },
  ];
  if (hasSelf) {
    series.push({
      name: 'Self rating',
      itemStyle: { color: ITPMETRICS_YELLOW },
      // 2026-06-08: self numbers hidden to prevent peer/self label overlap (see
      // the peer-series note above). The self polygon + legend still show it.
      label: { show: false },
      areaStyle: { color: ITPMETRICS_YELLOW_LIGHT },
      value: selfValues,
    });
  }

  const option = {
    legend: {
      orient: 'horizontal',
      bottom: 0,
      textStyle: { color: 'black', fontSize: 13 },
    },
    tooltip: {
      trigger: 'item',
      padding: [7, 10],
      backgroundColor: 'white',
      borderColor: 'gray-300',
      textStyle: { color: 'gray-1100' },
      borderWidth: 1,
    },
    radar: {
      indicator,
      // 2026-06-08: reduce radius + axis-name font and add label padding so the
      // six PLAHER axis names (notably the longer "Performance" / "Expression")
      // no longer slightly overlap their neighbours.
      radius: 118,
      splitLine: { lineStyle: { color: '#cccccc' } },
      axisName: { color: 'black', fontSize: 11, padding: [3, 6] },
    },
    series: [
      { type: 'radar', data: series },
    ],
  };

  return (
    <ReactEChartsCore
      echarts={echarts}
      option={option}
      style={{ height: '340px', width: '100%', maxWidth: '560px' }}
    />
  );
}

PlaherRadarChart.propTypes = {
  signals: PropTypes.shape({
    peer_means: PropTypes.object,
    self_ratings: PropTypes.object,
    // profile-driven axis order + display labels (individualReport/signals.js).
    dims: PropTypes.arrayOf(PropTypes.string),
    dim_labels: PropTypes.object,
    profile_id: PropTypes.string,
  }),
};

PlaherRadarChart.defaultProps = { signals: null };

export default PlaherRadarChart;
